import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query, queryAssignedElements } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import {
  Alignment,
  BackgroundShade,
  LinkTarget,
  ListItemStatus,
  ListItemVariant,
} from '@/common/types'
import { DdsElement } from '@/internal/dds-hu-element'

import DapDSCard from '../card/card.component'
import DapDSCardContent from '../card/card-content.component'
import DapDSIcon from '../icon/icon.component'
import styles from './list-item.scss?inline'

/**
 * `dap-ds-list-item` is a custom banner component.
 * @element dap-ds-list-item
 * @summary A list
 * @title - List item
 *
 * @slot - The content of the list item.
 * @slot icon - The icon of the list item.
 * @slot title - The title of the list item.
 * @slot actions - The actions of the list item.
 *
 * @csspart base - The main list item container.
 * @csspart icon - The icon of the list item.
 * @csspart title - The title of the list item.
 * @csspart description - The description of the list item.
 * @csspart container - The container of the list item.
 * @csspart list-item-base - The base list item container.
 * @csspart icon-container - The container of the icon.
 * @csspart title-container - The container of the title.
 * @csspart description-container - The container of the description.
 * @csspart actions-container - The container of the actions.
 * @csspart iteractive-indicator-container - The container of the interactive indicator.
 *
 * @cssproperty --dds-list-item-gap - Gap between list item elements (default: var(--dds-spacing-200))
 * @cssproperty --dds-list-item-content-gap - Gap between content elements (default: var(--dds-spacing-200))
 * @cssproperty --dds-list-item-horizontal-gap - Gap for horizontal alignment (default: var(--dds-spacing-300))
 * @cssproperty --dds-list-item-vertical-gap - Gap for vertical alignment (default: var(--dds-spacing-200))
 * @cssproperty --dds-list-item-icon-size - Size of the icon (default: var(--dds-spacing-600))
 * @cssproperty --dds-list-item-number-size - Size of the number icon (default: 20px)
 * @cssproperty --dds-list-item-actions-gap - Gap between action elements (default: var(--dds-spacing-400))
 * @cssproperty --dds-list-item-actions-padding - Padding for the actions container (default: var(--dds-spacing-100))
 *
 * @cssproperty --dds-list-item-background-base - Background color for base shade (default: var(--dds-background-neutral-base))
 * @cssproperty --dds-list-item-background-subtle - Background color for subtle shade (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-list-item-background-medium - Background color for medium shade (default: var(--dds-background-neutral-medium))
 * @cssproperty --dds-list-item-background-strong - Background color for strong shade (default: var(--dds-background-neutral-strong))
 *
 * @cssproperty --dds-list-item-icon-brand - Color for brand icon (default: var(--dds-icon-brand-subtle))
 * @cssproperty --dds-list-item-icon-neutral - Color for neutral icon (default: var(--dds-icon-neutral-base))
 * @cssproperty --dds-list-item-icon-positive - Color for positive icon (default: var(--dds-icon-positive-subtle))
 * @cssproperty --dds-list-item-icon-negative - Color for negative icon (default: var(--dds-icon-negative-subtle))
 *
 * @cssproperty --dds-list-item-title-color - Title text color (default: var(--dds-text-neutral-strong))
 * @cssproperty --dds-list-item-description-color - Description text color (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-list-item-number-text-color - Number text color (default: var(--dds-text-neutral-inverted))
 *
 */
export default class DapDSListItem extends DdsElement {
  static tagName = 'dap-ds-list-item'

  static dependencies = {
    'dap-ds-icon': DapDSIcon,
    'dap-ds-card': DapDSCard,
    'dap-ds-card-content': DapDSCardContent,
  }

  @queryAssignedElements({ slot: 'actions' })
  private readonly actionSlot: HTMLElement[]
  @query('.list-item__actions') private readonly actionContainer: HTMLElement

  /**
   * The variant of the list item.
   * @type {'info' | 'pass' | 'fail' | 'notapplicable' | 'empty' | 'number'}
   */
  @property() variant: ListItemVariant = 'info'

  /**
   * The background strongness of the list item.
   * @type {'none' | 'subtle' | 'base' | 'medium' | 'strong'}
   */
  @property() shade: BackgroundShade = 'base'

  /**
   * The status of the list item.
   * @type {'transparent' | 'brand' | 'neutral' | 'positive' | 'negative'}
   */
  @property() status: ListItemStatus = 'neutral'

  /**
   * The alignment of the list item.
   * @type {'horizontal' | 'vertical'}
   */
  @property() alignment: Alignment = 'horizontal'

  /**
   * The title of the list item.
   */
  @property() title: string

  /**
   * The icon of the list item. This is an icon name from the built in icons.
   */
  @property() icon: string

  /**
   * The number of the list item. Only used when variant is 'number'.
   */
  @property({ type: Number }) number: number

  /**
   * Whether the list item is interactive. Generates an anchor tag.
   */
  @property({ type: Boolean }) interactive: boolean

  /** The link target of the list item when interactive.
   * @type { '_blank' | '_self' | '_parent' | '_top' }
   */
  @property() target: LinkTarget = '_self'

  /** The href of the list item when interactive */
  @property() href: string

  /** The rel of the list item when interactive */
  @property() rel = 'noreferrer noopener'

  /**
   * Whether the list item has an icon.
   */
  @property({ type: Boolean }) noIcon: boolean

  /**
   * Whether the list item has no padding.
   */
  @property({ type: Boolean }) noPadding: boolean

  /**
   * The render as type of the list item, only applicable when interactive.
   * @type {'a' | 'button'}
   */
  @property({ type: String }) renderAs: 'a' | 'button' = 'a'

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (this.actionSlot && !this.actionSlot.length && this.actionContainer) {
      this.actionContainer.style.display = 'none'
    }
  }

  private readonly getIcon = (
    icon: string,
    variant: ListItemVariant,
    status: ListItemStatus,
    number?: number,
  ) => {
    let staticIcon: string

    if (icon) {
      return html`
        <dap-ds-icon
          class=${clsx('list-item__icon', {
            [`list-item__icon--${status}`]: status,
          })}
          name="${icon}"></dap-ds-icon>
      `
    }

    if (variant === 'number') {
      return html`
        <div
          class=${clsx('list-item__icon', {
            [`list-item__icon--${status}`]: status,
          })}>
          ${number}
        </div>
      `
    }

    switch (variant) {
      case 'info':
        staticIcon = 'lightbulb-fill'
        break
      case 'pass':
        staticIcon = 'checkbox-circle-fill'
        break
      case 'fail':
        staticIcon = 'close-circle-fill'
        break
      case 'notapplicable':
        staticIcon = 'forbid-fill'
        break
      case 'empty':
        staticIcon = 'checkbox-blank-circle-line'
        break
      default:
        staticIcon = 'lightbulb-fill'
        break
    }

    return html`
      <dap-ds-icon
        class=${clsx('list-item__icon', {
          [`list-item__icon--${status}`]: status,
        })}
        name="${staticIcon}"></dap-ds-icon>
    `
  }

  render() {
    return html`
      <dap-ds-card
        noBorder
        ?interactive=${this.interactive}
        renderAs=${this.renderAs}
        target=${this.target}
        href=${ifDefined(this.href)}
        rel=${this.target}
        ?noPadding=${this.noPadding}
        part="base"
        exportparts="base:list-item-base"
        class=${clsx(
          'list-item',
          `list-item--${this.variant}`,
          `list-item--${this.status}`,
          `list-item--${this.shade}`,
          `list-item--${this.alignment}`,
        )}>
        <dap-ds-card-content spacing="none" exportparts="base:card-base">
          <div class=${clsx('list-item__container')} part="container">
            ${!this.noIcon
              ? html`<div class="list-item__icon" part="icon-container">
                  <slot part="icon" name="icon">
                    ${this.getIcon(
                      this.icon,
                      this.variant,
                      this.status,
                      this.number,
                    )}
                  </slot>
                </div>`
              : nothing}
            <div class=${clsx('list-item__content')}>
              ${this.title
                ? html`<span
                    part="title"
                    class="list-item__title"
                    part="title-container">
                    <slot name="title">${this.title}</slot>
                  </span>`
                : nothing}
              <span
                part="description"
                class="list-item__description"
                part="description-container">
                <slot></slot>
              </span>
              <span
                part="actions"
                class="list-item__actions"
                part="actions-container">
                <slot name="actions" part="actions"></slot>
              </span>
            </div>
            ${this.interactive
              ? html`<div
                  class="list-item__iteractive-indicator"
                  part="iteractive-indicator-container">
                  <dap-ds-icon name="arrow-right-l-line"></dap-ds-icon>
                </div>`
              : nothing}
          </div>
        </dap-ds-card-content>
      </dap-ds-card>
    `
  }
}
